import React, { useMemo } from 'react';
import { CookieDisclaimer } from 'molecules';
import { Appbar, Footer, VerifyEmailModal } from 'organisms';
import { VerifyEmailModalProvider } from '../organisms/VerifyEmailModal/VerifyEmailModalContext';
import bugsnagClient from 'lib/errors/bugsnag';
import { AppProps } from 'next/app';
import Script from 'next/script';
import { useRouter } from 'next/router';
import Error from './_error';
import NotFoundPage from './404';
import { reportError } from 'lib/errors';
import { ROUTES } from 'config/Nav';
import {
  ElevatorProvider,
  lightTheme,
  darkTheme,
  useColorMode,
  ColorModeProvider,
  resolveHydrationColorMode,
  isBugsnagActive,
} from '@elevator/ui';
import { CacheProvider, EmotionCache } from '@emotion/react';
import createEmotionCache from '../util/createEmotionCache';
import { useGooglePublisherTag } from '../hooks/useGooglePublisherTag';
import { makeStyles } from 'tss-react/mui';
import Stack from '@mui/material/Stack';

const clientSideEmotionCache = createEmotionCache();

const useLayoutStyles = makeStyles()((theme) => ({
  pageFrame: {
    minHeight: '100vh',
    position: 'relative',
  },
  pageContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
    flex: 1,
    minHeight: 0,
    [theme.breakpoints.up('md')]: {
      gap: theme.spacing(6),
    },
  },
}));

/**
 * Props for the custom App component
 */
interface MyAppProps extends AppProps {
  /**
   * Optional Emotion cache instance (used for SSR)
   * Defaults to client-side cache if not provided
   */
  emotionCache?: EmotionCache;
  /**
   * Initial color mode for the app (light or dark)
   * Defaults to 'light' if not provided
   */
  initialColorMode?: 'light' | 'dark';
}

/**
 * ErrorBoundary component type from Bugsnag
 */
type BugsnagErrorBoundary = React.ComponentType<{
  FallbackComponent: React.ComponentType<{ statusCode?: number }>;
  children: React.ReactNode;
}>;

/**
 * ErrorBoundary component from Bugsnag (if enabled)
 * Used to catch and report React errors
 */
const ErrorBoundary: BugsnagErrorBoundary | undefined =
  isBugsnagActive() && bugsnagClient
    ? (bugsnagClient
        .getPlugin('react')
        ?.createErrorBoundary(React) as BugsnagErrorBoundary)
    : undefined;

/**
 * Props for the AppContent component
 */
interface AppContentProps {
  /**
   * Next.js page component to render
   */
  Component: AppProps['Component'];
  /**
   * Props to pass to the page component
   */
  pageProps: AppProps['pageProps'];
}

/**
 * Inner component that uses the color mode context
 *
 * This component is separated from the main App component to allow
 * access to the ColorModeProvider context via useColorMode hook.
 *
 * Features:
 * - Initializes Google Publisher Tag (GPT) for ad serving
 * - Loads GPT script asynchronously
 * - Provides theme based on current color mode
 * - Renders page component with global providers
 */
const AppContent = ({ Component, pageProps }: AppContentProps): React.JSX.Element => {
  const { mode } = useColorMode();
  const router = useRouter();
  const { classes } = useLayoutStyles();

  // Initialize Google Publisher Tag (GPT) for ad serving
  useGooglePublisherTag();

  // Use the pre-configured light or dark theme from UI package
  const theme = useMemo(() => {
    return mode === 'dark' ? darkTheme : lightTheme;
  }, [mode]);

  // Validate required environment variables
  const apiHost = process.env.API_URL ? new URL(process.env.API_URL).origin : '';

  if (!apiHost && process.env.NODE_ENV !== 'production') {
    console.warn('API_URL environment variable is not set');
  }

  const isExcludedPage = Component === NotFoundPage || Component === Error;
  const isExcludedRoute =
    router.route === ROUTES.PAGE_NOT_FOUND.to || router.route === ROUTES.ERROR.to;
  const excludeContainer = isExcludedPage || isExcludedRoute;

  const globalOverlays = (
    <>
      <VerifyEmailModal />
      <CookieDisclaimer />
    </>
  );

  return (
    <>
      {/* Load Google Publisher Tag (GPT) library */}
      <Script
        src="https://securepubads.g.doubleclick.net/tag/js/gpt.js"
        strategy="afterInteractive"
      />

      <ElevatorProvider
        apiHost={apiHost}
        theme={theme}
        reportError={reportError}
        googleAnalyticsTrackingCode={process.env.GA4_ID || undefined}
      >
        <VerifyEmailModalProvider>
          {excludeContainer ? (
            <>
              <Component {...pageProps} />
              {globalOverlays}
            </>
          ) : (
            <>
              <Stack
                direction="column"
                className={classes.pageFrame}
                suppressHydrationWarning
              >
                <Appbar />
                <Stack className={classes.pageContainer}>
                  <Component {...pageProps} />
                  <Footer />
                </Stack>
              </Stack>
              {globalOverlays}
            </>
          )}
        </VerifyEmailModalProvider>
      </ElevatorProvider>
    </>
  );
};

/**
 * Main App Component
 *
 * Next.js custom App component that wraps all pages with necessary providers.
 *
 * Features:
 * - Emotion CSS-in-JS cache provider (for SSR compatibility)
 * - Color mode provider (light/dark theme switching)
 * - Error boundary (Bugsnag if enabled)
 * - Theme initialization based on SSR color mode
 *
 * This component runs on both server and client, ensuring consistent
 * rendering between SSR and client-side hydration.
 *
 * @param props - Next.js App props with custom extensions
 * @param props.Component - The page component to render
 * @param props.pageProps - Props for the page component
 * @param props.emotionCache - Optional Emotion cache (from SSR)
 * @param props.initialColorMode - Initial color mode (from SSR)
 * @returns App component with all providers
 */
const App = ({
  Component,
  pageProps,
  emotionCache = clientSideEmotionCache,
  initialColorMode,
}: MyAppProps): React.JSX.Element => {
  const hydrationColorMode = resolveHydrationColorMode(initialColorMode);

  const AppTree = (
    <CacheProvider value={emotionCache}>
      <ColorModeProvider initialMode={hydrationColorMode}>
        <AppContent Component={Component} pageProps={pageProps} />
      </ColorModeProvider>
    </CacheProvider>
  );

  return ErrorBoundary ? (
    <ErrorBoundary FallbackComponent={Error}>{AppTree}</ErrorBoundary>
  ) : (
    AppTree
  );
};

export default App;
